import FileBrowser, {
  FileData,
  UseLoadFile,
  UseLoadFileUrl,
} from '@wandb/weave/common/components/FileBrowser';
import React, {useCallback, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {useRunFilesQuery} from '../generated/graphql';
import {
  FileDownloadLocation,
  trackFileDownload,
} from '../services/analytics/fileDownloadEvents';
import {File} from '../state/graphql/runFilesQuery';
import {useSingleFileQuery} from '../state/graphql/singleFileQuery';
import {MAX_NUM_FILES} from '../util/constants';
import {trackPageViewed} from '../util/navigation';
import {useLoadFile} from '../util/requests';
import * as Urls from '../util/urls';
import {TabWaveLoader} from './RunDetails/TabWaveLoader';

// Handles multipart files and optional custom labels.
const CONSOLE_LOGS_FILENAME_REGEX = /^(logs\/)?output(.+)?\.log$/;

interface RunFilesProps {
  isCodeHidden?: boolean;
  entityName: string;
  projectName: string;
  runName: string;
  path?: string;
  history: any;
  setSelectedRunName?: (name: string) => void;
}

const RunFiles = (props: RunFilesProps) => {
  const location = useLocation();
  const urlParams = useParams<{
    groupName?: string;
    sweepName?: string;
    tab?: string;
    runTab?: string;
  }>();

  const isGroupPage = urlParams.groupName != null;
  const isSweepPage = urlParams.sweepName != null;
  // for page view tracking
  useEffect(() => {
    let pageName = 'Run';
    if (urlParams.groupName != null) {
      pageName = 'Group run';
    } else if (urlParams.sweepName != null) {
      pageName = 'Sweep run';
    }
    trackPageViewed(pageName, 'Files');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    entityName,
    projectName,
    runName,
    history,
    path,
    setSelectedRunName = () => null,
  } = props;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRunName = params.get('runName');
    if (urlRunName) {
      setSelectedRunName(urlRunName);
    }
  }, [setSelectedRunName, location.search]);

  const setFilePath = useCallback(
    (newPathArray: string[]) => {
      // files tab needs to be hard coded because could be in code tab with no code.codeArtifact, and we should
      // go to the files tab instead of code tab in this case
      history.push(
        urlParams.groupName && isGroupPage
          ? Urls.runGroup({
              entityName,
              projectName,
              name: urlParams.groupName,
              tab: urlParams.tab,
              runTab: 'files',
              filePath: newPathArray,
              runName,
            })
          : urlParams.sweepName && isSweepPage
            ? Urls.runSweep({
                entityName,
                projectName,
                name: urlParams.sweepName,
                tab: urlParams.tab,
                runTab: 'files',
                filePath: newPathArray,
                runName,
              })
            : Urls.runTabFile(entityName, projectName, runName, newPathArray)
      );
    },
    [
      entityName,
      projectName,
      runName,
      history,
      isGroupPage,
      urlParams.groupName,
      urlParams.sweepName,
      isSweepPage,
      urlParams.tab,
    ]
  );

  const handleDownload = useCallback(
    (file: FileData) => {
      const location: FileDownloadLocation = urlParams.sweepName
        ? 'sweep-files'
        : urlParams.groupName
          ? 'group-files'
          : 'run-files';

      if (CONSOLE_LOGS_FILENAME_REGEX.test(file.name)) {
        trackFileDownload({
          fileType: 'log',
          isMultipartLogsFile: file.name.startsWith('logs/'),
          location,
        });
      }
    },
    [urlParams.groupName, urlParams.sweepName]
  );

  const runFilesQuery = useRunFilesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityName,
      projectName,
      runName,
      maxNumFiles: MAX_NUM_FILES,
    },
  });

  const files =
    runFilesQuery.data?.project?.run?.files?.edges.map(e => e.node as File) ??
    [];

  if (runFilesQuery.loading && files.length === 0) {
    return <TabWaveLoader />;
  }

  const useLoadRunFile: UseLoadFile = (file, options) => {
    return useLoadFile({entityName, projectName, runName}, file.name, options);
  };
  const useLoadRunFileUrl: UseLoadFileUrl = file => {
    return useSingleFileQuery({
      entityName,
      projectName,
      runName,
      fileName: file.name,
    });
  };

  return (
    <FileBrowser
      totalFiles={runFilesQuery.data?.project?.run?.fileCount ?? 0}
      isCodeHidden={props.isCodeHidden}
      files={files}
      path={path}
      history={history}
      useLoadFile={useLoadRunFile}
      useLoadFileUrl={useLoadRunFileUrl}
      setFilePath={setFilePath}
      onDownload={handleDownload}
    />
  );
};

export default RunFiles;
